import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, X, Sun, Trees, User, Crop, Sparkles } from "lucide-react";

const TIP_ICONS = [Sun, Trees, User, Crop, Sparkles];

/**
 * Hero photo recommendation card + cheatsheet modal.
 * Lives at the top of the Vendor Dashboard. Producers with grove photos get
 * clicked ~38% more — we surface this as a friendly nudge with concrete tips.
 * Dismissal persists in localStorage so we don't nag once they've acknowledged.
 */
const STORAGE_KEY = "oliveoil_hero_tips_dismissed";

export const HeroPhotoTipsCard = () => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(STORAGE_KEY) === "1";
  });
  const [open, setOpen] = useState(false);

  if (dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  // 5 tips keyed off i18n so they translate. Each = title + body.
  const tips = [1, 2, 3, 4, 5].map((i) => ({
    title: t(`heroTips.tips.t${i}.title`),
    body: t(`heroTips.tips.t${i}.body`),
    Icon: TIP_ICONS[i - 1],
  }));

  return (
    <div
      className="mt-6 relative rounded-sm overflow-hidden bg-gradient-to-r from-olive/15 via-olive/8 to-transparent border border-olive/25 p-5 pr-12"
      data-testid="hero-tips-card"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 text-ink-muted hover:text-ink-soft hover:bg-bone/60 rounded-full transition-colors"
        data-testid="hero-tips-dismiss"
        aria-label={t("common.cancel")}
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-olive text-bone flex items-center justify-center flex-shrink-0">
          <Camera className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="font-serif text-xl leading-snug" data-testid="hero-tips-headline">
            {t("heroTips.headline")}
          </p>
          <p className="text-sm text-ink-soft mt-1">{t("heroTips.subtext")}</p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="mt-3 inline-flex items-center gap-1 text-sm text-olive hover:underline font-medium"
            data-testid="hero-tips-see"
          >
            {t("heroTips.seeTips")} →
          </button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-bone max-h-[90vh] overflow-y-auto" data-testid="hero-tips-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">{t("heroTips.dialogTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink-soft -mt-1 mb-4">{t("heroTips.dialogIntro")}</p>

          <ol className="space-y-5">
            {tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-4" data-testid={`hero-tip-${i + 1}`}>
                <div className="w-10 h-10 rounded-full bg-olive/10 text-olive flex items-center justify-center flex-shrink-0">
                  <tip.Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-serif text-lg leading-snug">
                    <span className="font-mono text-xs text-ink-muted mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {tip.title}
                  </p>
                  <p className="text-sm text-ink-soft mt-1 leading-relaxed">{tip.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeroPhotoTipsCard;
